import React, { Fragment, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";

import Header from "../../components/header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import aImg from "../../images/about/about_image_3.webp";
import aImg2 from "../../images/about/about_image_4.webp";
import ModalVideo from "react-modal-video";
import PolicySection from "./Policy";
import WhyUs from "./WhyUs";
import FeaturesSection from "../../components/FeaturesSection/FeaturesSection";
import TeamSection from "../../components/TeamSection/TeamSection";
import CtaSection from "../../components/CtaSection/CtaSection";

import { useSeo } from "../../hooks/useSeo";

// Static fallbacks if API not available or no matching SEO entry
const staticMeta = {
  title: "About RashTech Info | Company, Mission, Team",
  description:
    "Learn about RashTech Info—our mission, values, services, and the team powering our solutions. Discover why organizations choose us.",
  url: "https://rashtechinfo.com/about",
  image: "https://rashtechinfo.com/og-image.png", // replace with your real asset if needed
  siteName: "RashTech Info",
  keywords:
    "RashTech Info, about, company, team, mission, vision, services, IT solutions",
  themeColor: "#0A0D1C",
};

const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: staticMeta.siteName,
  url: "https://rashtechinfo.com/",
  logo: "https://rashtechinfo.com/logo.png",
  sameAs: [
    "https://www.facebook.com/rashtechinfo",
    "https://www.linkedin.com/company/rashtechinfo",
    "https://twitter.com/rashtechinfo",
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "sales",
      email: "sales@rashtechinfo.com",
      telephone: "+91-000-000-0000",
      areaServed: "IN",
      availableLanguage: ["en"],
    },
  ],
};

const AboutUsPage = () => {
  const [isOpen, setOpen] = useState(false);

  // Fetch all SEO entries from your API and map to current route (/about)
  const { seo, loading, error } = useSeo("/getSeo");

  // Merge dynamic SEO over static defaults
  const computed = useMemo(() => {
    const dynamic = {
      title: seo?.title,
      description: seo?.metaDescription,
      keywords: seo?.metaKeywords,
      canonicalUrl: seo?.canonicalUrl,
    };

    return {
      title: dynamic.title || staticMeta.title,
      description: dynamic.description || staticMeta.description,
      keywords: dynamic.keywords || staticMeta.keywords,
      url: dynamic.canonicalUrl || staticMeta.url,
      image: staticMeta.image,
      siteName: staticMeta.siteName,
      themeColor: staticMeta.themeColor,
    };
  }, [seo]);

  return (
    <Fragment>
      <Helmet>
        {/* Primary */}
        <title>{computed.title}</title>
        <meta name="description" content={computed.description} />
        {computed.keywords && (
          <meta name="keywords" content={computed.keywords} />
        )}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content={computed.themeColor} />
        {computed.url && <link rel="canonical" href={computed.url} />}

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={computed.siteName} />
        <meta property="og:title" content={computed.title} />
        <meta property="og:description" content={computed.description} />
        {computed.url && <meta property="og:url" content={computed.url} />}
        {computed.image && <meta property="og:image" content={computed.image} />}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={computed.title} />
        <meta name="twitter:description" content={computed.description} />
        {computed.image && (
          <meta name="twitter:image" content={computed.image} />
        )}

        {/* Optional preconnects */}
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin=""
        />
        <link rel="preconnect" href="https://fonts.googleapis.com" />

        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(orgJsonLd)}
        </script>
      </Helmet>

      <Header />
      <main className="page_content about-page">
        <PageTitle
          pageTitle={"About Us"}
          pagesub={"RashTech Info"}
          pageTop={"About More "}
        />

        <section className="intro_about_section section_space bg-light">
          <div className="intro_about_content">
            <div className="container">
              <div className="row">
                <div className="col-lg-8">
                  <div className="image_wrap">
                    <img src={aImg} alt="RashTech Info - About " />
                  </div>
                </div>
                <div className="col-lg-4">
                  <div className="image_wrap position-relative">
                    <img src={aImg2} alt="RashTech Info - About" />
                    <button
                      className="video_btn ripple_effect"
                      onClick={() => setOpen(true)}
                    >
                      <span className="btn_icon">
                        <i className="fa-solid fa-play"></i>
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="container">
            <div className="heading_block mb-0">
              <div className="row justify-content-lg-between">
                <div className="col-lg-4">
                  <div className="heading_focus_text">
                    About
                    <span className="badge bg-secondary text-white">
                      RashTech Info
                    </span>
                  </div>
                  <h2 className="heading_text mb-0">
                    We Provide You Flexible Services
                  </h2>
                </div>
                <div className="col-lg-6">
                  <p className="heading_description mb-0">
                    At RashTech Info, we pride ourselves on offering dynamic and
                    IT to suit your specific requirements. Our flexible approach
                    means we can adjust our offerings to accommodate your
                    evolving needs, whether you require comprehensive IT
                    infrastructure support, strategic consultancy, software
                    development
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <PolicySection />
        <FeaturesSection />
        <TeamSection />
        <WhyUs />
        <CtaSection />
      </main>

      <Footer />
      <Scrollbar />
      <ModalVideo
        channel="youtube"
        autoplay
        isOpen={isOpen}
        videoId="q1wQjhQpVJQ"
        onClose={() => setOpen(false)}
      />

      {/* Optional hidden diagnostics */}
      {error && <div style={{ display: "none" }}>SEO load error</div>}
      {loading && <div style={{ display: "none" }}>Loading SEO…</div>}
    </Fragment>
  );
};

export default AboutUsPage;
